import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { EntityLayout } from '@backstage/plugin-catalog';
import {
  commonOverviewContent,
  commonRoutes,
  FixedHeightGrid,
  ScrollingFixedHeightGrid,
} from './common';

import { EntityHasApisCard } from '@backstage/plugin-api-docs';
import {
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntitySwitch,
} from '@backstage/plugin-catalog';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { PropsWithChildren, useMemo } from 'react';

import {
  EntityGrafanaAlertsCard,
  EntityGrafanaDashboardsCard,
  EntityOverviewDashboardViewer,
  isDashboardSelectorAvailable,
  isOverviewDashboardAvailable,
} from '@backstage-community/plugin-grafana';
import { LinkButton } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityKubernetesContent } from '@backstage/plugin-kubernetes';
import { CardContent, CardHeader } from '@material-ui/core';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import { default as Grid } from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import useAsync from 'react-use/esm/useAsync';

const grafanaContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case
        if={entity => Boolean(isDashboardSelectorAvailable(entity))}
      >
        <FixedHeightGrid
          size={6}
          sx={{
            '*[class^="MuiPaper-root"]': {
              /* The plugin card does not have a height set, which causes grid misalignment */
              height: '100%',
              '*[class^="MuiTypography-root"] > p': {
                /* The plugin card nests a <p> inside <Typography> which causes inconsistent styling */
                font: 'unset',
              },
            },
          }}
        >
          <EntityGrafanaDashboardsCard />
        </FixedHeightGrid>
        <FixedHeightGrid
          size={6}
          sx={{
            '*[class^="MuiPaper-root"]': {
              /* The plugin card does not have a height set, which causes grid misalignment */
              height: '100%',
            },
          }}
        >
          <EntityGrafanaAlertsCard showState />
        </FixedHeightGrid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={isOverviewDashboardAvailable}>
        <Grid size={12}>
          <EntityOverviewDashboardViewer />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const IsOwnerErrorBoundary = (props: PropsWithChildren) => {
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);

  const { value: ownershipEntityRefs } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    return identity.ownershipEntityRefs ?? [];
  });

  const isOwner = useMemo(() => {
    if (!ownershipEntityRefs) return false;
    if (!entity?.spec?.owner) return true; // No entity or unowned, allow

    if (ownershipEntityRefs?.includes(entity.spec.owner as string)) return true;
    return false;
  }, [ownershipEntityRefs, entity]);

  return isOwner ? (
    props.children
  ) : (
    <Card title="You don't own this component">
      <CardHeader title="You don't own this component" />
      <CardContent>
        <Grid container spacing={1} direction="column" alignItems="start">
          <Typography>
            Access to this page is currently restricted to members of the group
            which owns this component.
          </Typography>
          <Divider />
          <LinkButton variant="outlined" to="../overview">
            Back to overview
          </LinkButton>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} size={12} alignItems="stretch">
        {commonOverviewContent}
        {grafanaContent}
        <ScrollingFixedHeightGrid size={4}>
          <EntityHasComponentsCard />
        </ScrollingFixedHeightGrid>
        <ScrollingFixedHeightGrid size={4}>
          <EntityHasApisCard />
        </ScrollingFixedHeightGrid>
        <ScrollingFixedHeightGrid size={4}>
          <EntityHasResourcesCard />
        </ScrollingFixedHeightGrid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/kubernetes" title="Kubernetes">
      <IsOwnerErrorBoundary>
        <EntityKubernetesContent refreshIntervalMs={30000} />
      </IsOwnerErrorBoundary>
    </EntityLayout.Route>

    {...commonRoutes}
  </EntityLayout>
);
